import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TokenExpirationService } from './services/token-expiration.service';
import { AuthService } from './services/auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  showWarning = false;
  timeRemaining: number | null = null;
  formattedTime = '00:00';
  notification: { message: string; type: 'error' | 'success' } | null = null;
  
  private warningSubscription?: Subscription;
  private timeRemainingSubscription?: Subscription;
  private notificationSubscription?: Subscription;
  private authStateSubscription?: Subscription;

  constructor(
    private tokenExpirationService: TokenExpirationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Start monitoring if user is already authenticated
    if (this.authService.isAuthenticated()) {
      this.tokenExpirationService.startMonitoring();
    }

    // Subscribe to warning state
    this.warningSubscription = this.tokenExpirationService.showWarning$.subscribe(
      (show) => {
        this.showWarning = show;
      }
    );

    // Subscribe to time remaining
    this.timeRemainingSubscription = this.tokenExpirationService.timeRemaining$.subscribe(
      (time) => {
        this.timeRemaining = time;
        if (time !== null) {
          this.formattedTime = this.tokenExpirationService.formatTime(time);
        }
      }
    );

    // Subscribe to notifications
    this.notificationSubscription = this.tokenExpirationService.notification$.subscribe(
      (notification) => {
        this.notification = notification;
        // Auto-hide notification after 5 seconds
        if (notification) {
          setTimeout(() => {
            this.notification = null;
          }, 5000);
        }
      }
    );

    // Monitor auth state changes to start/stop monitoring
    this.authStateSubscription = this.authService.currentUser$.subscribe((user) => {
      if (user) {
        this.tokenExpirationService.startMonitoring();
      } else {
        this.tokenExpirationService.stopMonitoring();
      }
    });
  }

  ngOnDestroy(): void {
    this.warningSubscription?.unsubscribe();
    this.timeRemainingSubscription?.unsubscribe();
    this.notificationSubscription?.unsubscribe();
    this.authStateSubscription?.unsubscribe();
    this.tokenExpirationService.stopMonitoring();
  }

  onLogout(): void {
    this.authService.logout();
  }

  onRefresh(): void {
    // Extend token by 5 minutes
    this.authService.refreshToken().subscribe({
      next: (response) => {
        // Token refreshed successfully, restart monitoring with new token
        this.tokenExpirationService.refresh();
        
        // Show success notification
        this.notification = {
          message: `Session extended successfully. You have ${response.expiresIn} more minutes.`,
          type: 'success'
        };
        
        // Auto-hide notification after 3 seconds
        setTimeout(() => {
          this.notification = null;
        }, 3000);
      },
      error: (err) => {
        console.error('Error refreshing token:', err);
        
        // Show error notification
        this.notification = {
          message: 'Failed to extend session. Please login again.',
          type: 'error'
        };
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          this.notification = null;
        }, 5000);
      }
    });
  }
}

